import {
    Box,
    Grid,
    Toolbar,
    Typography,
    FormControl,
    Select,
    MenuItem,
    CircularProgress,
    Button,
    Pagination,
    TextField,
    Card,
    CardContent,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Stack,
    Chip,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { CloudDownloadOutlined, KeyboardArrowLeftOutlined } from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import { useNavigate } from "react-router-dom";

function AddonSales() {
    const token = localStorage.getItem("token");
    const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");
    const navigate = useNavigate();

    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);

    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedAddonGroup, setSelectedAddonGroup] = useState("");
    const [selectedAddonValue, setSelectedAddonValue] = useState("");

    const [selectedStartDate, setSelectedStartDate] = useState(new Date());
    const [selectedEndDate, setSelectedEndDate] = useState(new Date());

    const [reportRows, setReportRows] = useState([]);
    const [categories, setCategories] = useState([]);

    const [addonGroups, setAddonGroups] = useState([]);

    const itemsPerPage = 50;
    const [pagination, setPagination] = useState({
        totalDocuments: 0,
        limit: itemsPerPage,
        NumberOfPages: 1,
        currentPage: 1,
    });
    const [currentPage, setCurrentPage] = useState(1);

    const headers = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const normalizeAddons = (raw) => {
        const arr = Array.isArray(raw) ? raw : [];

        return arr.map((a) => {
            const valuesArr =
                a?.values ||
                a?.addonValues ||
                a?.items ||
                a?.addonValueList ||
                [];

            return {
                _id: a?._id,
                title: a?.title || a?.name || a?.addonTitle || "Addon",
                values: (valuesArr || []).map((v) => ({
                    _id: v?._id,
                    title: v?.value || v?.name || v?.title || v?.selectedValue || "Value",
                })),
            };
        });
    };

    const getCategories = async () => {
        try {
            const response = await axios.get(`${API.BASE_URL}masters/category/get`, {
                headers,
            });
            const cats = response?.data?.data?.categories || [];
            setCategories(cats);
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to fetch categories", { autoClose: 3000 });
        }
    };

    const getAddons = async () => {
        try {
            const response = await axios.get(`${API.BASE_URL}addons`, { headers });

            const raw =
                response?.data?.data?.addons ||
                response?.data?.data?.addonGroups ||
                response?.data?.data ||
                [];

            const normalized = normalizeAddons(raw);

            normalized.sort((a, b) => (a.title || "").localeCompare(b.title || ""));

            normalized.forEach((g) =>
                g.values.sort((a, b) => (a.title || "").localeCompare(b.title || ""))
            );

            setAddonGroups(normalized);
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to fetch addons", { autoClose: 3000 });
        }
    };

    const getAddonSales = async (page = 1, download = false) => {
        if (!outletData?._id) return;

        if (
            selectedStartDate &&
            selectedEndDate &&
            selectedStartDate > selectedEndDate
        ) {
            toast.error("From Date cannot be greater than To Date", {
                autoClose: 3000,
            });
            return;
        }

        setLoading(true);

        try {
            const updatedStartDate = format(selectedStartDate, "yyyy-MM-dd");
            const updatedEndDate = format(selectedEndDate, "yyyy-MM-dd");

            const queryParams = new URLSearchParams();
            queryParams.append("startDate", updatedStartDate);
            queryParams.append("endDate", updatedEndDate);
            queryParams.append("outlet", outletData?._id);
            queryParams.append("page", String(page));
            queryParams.append("limit", String(itemsPerPage));

            if (selectedCategory) queryParams.append("category", selectedCategory);
            if (selectedAddonGroup) queryParams.append("addonId", selectedAddonGroup);
            if (selectedAddonValue)
                queryParams.append("addonValueId", selectedAddonValue);

            if (download) queryParams.append("isDownload", "true");

            const response = await axios.get(
                `${API.BASE_URL}addons/addon-sales-report-for-outlet?${queryParams.toString()}`,
                {
                    headers,
                    responseType: download ? "blob" : "json",
                }
            );

            if (download) {
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const link = document.createElement("a");
                link.href = window.URL.createObjectURL(blob);

                const selectedOutletName =
                    outletData?.name || outletData?.outletName || "Outlet";

                const fileName = `${selectedOutletName} Addon Sales Report (${updatedStartDate} to ${updatedEndDate}).xlsx`;

                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);

                toast.success("Report Excel file downloaded successfully!", {
                    position: "bottom-center",
                    autoClose: 3000,
                });

                return;
            }

            const preview = response?.data?.data || [];
            const pag =
                response?.data?.pagination || response?.data?.data?.pagination || {};

            setReportRows(preview);

            setPagination({
                totalDocuments: pag.totalDocuments || preview.length,
                limit: pag.limit || itemsPerPage,
                NumberOfPages: pag.NumberOfPages || 1,
                currentPage: pag.currentPage || page,
            });

            setCurrentPage(pag.currentPage || page);
        } catch (error) {
            console.error("Error:", error);
            if (!download) setReportRows([]);

            toast.error(download ? error.response.data.error || "Failed to download report" : error.response.data.error || "Failed to fetch report", {
                autoClose: 3000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        setCurrentPage(1);
    };

    const handleAddonGroupChange = (event) => {
        const addonId = event.target.value;
        setSelectedAddonGroup(addonId);

        setSelectedAddonValue("");
        setCurrentPage(1);
    };

    const handleAddonValueChange = (event) => {
        setSelectedAddonValue(event.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
        getAddonSales(page);
    };

    const addonValuesForSelectedGroup = useMemo(() => {
        if (!selectedAddonGroup) return [];
        const group = addonGroups.find((g) => g._id === selectedAddonGroup);
        return group?.values || [];
    }, [addonGroups, selectedAddonGroup]);

    const sortedCategories = useMemo(() => {
        return [...(categories || [])].sort((a, b) => {
            const sa = Number(a?.sequenceNumber ?? 999999);
            const sb = Number(b?.sequenceNumber ?? 999999);
            return sa - sb;
        });
    }, [categories]);

    const filteredRows = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return reportRows;

        return (reportRows || []).filter((row) => {
            const product = (row?.productName || "").toLowerCase();
            const cat = (row?.categoryName || "").toLowerCase();
            const addonTitle = (row?.addonTitle || "").toLowerCase();
            return product.includes(q) || cat.includes(q) || addonTitle.includes(q);
        });
    }, [reportRows, searchText]);

    const legendTotals = useMemo(() => {
        const rows = filteredRows || [];

        return rows.reduce(
            (acc, r) => {
                acc.totalAddonSalesCount += Number(r?.totalAddonSalesCount || 0);
                acc.totalTaxable += Number(r?.totalTaxable || 0);
                acc.totalDiscount += Number(r?.totalDiscount || 0);
                acc.totalTaxAmount += Number(r?.totalTaxAmount || 0);
                acc.totalGrandTotal += Number(r?.totalGrandTotal || 0);
                return acc;
            },
            {
                totalAddonSalesCount: 0,
                totalTaxable: 0,
                totalDiscount: 0,
                totalTaxAmount: 0,
                totalGrandTotal: 0,
            }
        );
    }, [filteredRows]);

    useEffect(() => {
        getCategories();
        getAddons();
        getAddonSales(1);
    }, []);

    return (
        <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
            <Toolbar />

            <Grid container justifyContent="center" sx={{ pb: 6 }}>
                <Grid item xs={12} md={11}>
                    <Card
                        elevation={0}
                        sx={{
                            mt: 4,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
                        }}
                    >
                        <CardContent sx={{ py: 3 }}>
                            <Stack
                                direction={{ xs: "column", md: "row" }}
                                alignItems={{ xs: "flex-start", md: "center" }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Stack flexDirection={'row'}>
                                    <KeyboardArrowLeftOutlined sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} fontSize="large" />
                                    <Box>
                                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                                            Addon Sales Reports
                                        </Typography>
                                        <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                            Filter by category, addon group/value and date range, then view or download the report.
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Chip
                                    icon={<FilterAltOutlined />}
                                    label={`Records: ${filteredRows?.length || 0}`}
                                    sx={{ fontWeight: 700, borderRadius: 2 }}
                                    variant="outlined"
                                />
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            top: 70,
                            zIndex: 5,
                            background: "#fff",
                        }}
                    >
                        <CardContent sx={{ py: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                {/* Category */}
                                <Grid item xs={12} md={2.5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Category
                                    </Typography>

                                    <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                                        <Select value={selectedCategory} onChange={handleCategoryChange} displayEmpty>
                                            <MenuItem value="">
                                                <em>All</em>
                                            </MenuItem>

                                            {sortedCategories?.map((cat) => (
                                                <MenuItem key={cat?._id} value={cat?._id}>
                                                    {cat?.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Addon Group */}
                                <Grid item xs={12} md={2.5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Addon Group
                                    </Typography>

                                    <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                                        <Select value={selectedAddonGroup} onChange={handleAddonGroupChange} displayEmpty>
                                            <MenuItem value="">
                                                <em>All</em>
                                            </MenuItem>

                                            {addonGroups?.map((g) => (
                                                <MenuItem key={g?._id} value={g?._id}>
                                                    {g?.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Addon Value */}
                                <Grid item xs={12} md={2.5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Addon Value
                                    </Typography>

                                    <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                                        <Select
                                            value={selectedAddonValue}
                                            onChange={handleAddonValueChange}
                                            displayEmpty
                                            disabled={!selectedAddonGroup}
                                        >
                                            <MenuItem value="">
                                                <em>{selectedAddonGroup ? "All" : "Select group first"}</em>
                                            </MenuItem>

                                            {addonValuesForSelectedGroup?.map((v) => (
                                                <MenuItem key={v?._id} value={v?._id}>
                                                    {v?.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* From Date */}
                                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        From Date
                                    </Typography>

                                    <DatePicker
                                        selected={selectedStartDate}
                                        onChange={(date) => setSelectedStartDate(date)}
                                        dateFormat="yyyy-MM-dd"
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={100}
                                        customInput={
                                            <TextField
                                                fullWidth
                                                value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                                                InputProps={{ readOnly: true }}
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" } }}
                                            />
                                        }
                                    />
                                </Grid>

                                {/* To Date */}
                                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        To Date
                                    </Typography>

                                    <DatePicker
                                        selected={selectedEndDate}
                                        onChange={(date) => setSelectedEndDate(date)}
                                        dateFormat="yyyy-MM-dd"
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={100}
                                        customInput={
                                            <TextField
                                                fullWidth
                                                value={selectedEndDate ? format(selectedEndDate, "yyyy-MM-dd") : ""}
                                                InputProps={{ readOnly: true }}
                                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" } }}
                                            />
                                        }
                                    />
                                </Grid>

                                {/* Search */}
                                <Grid item xs={12} md={5}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                                        Search
                                    </Typography>

                                    <TextField
                                        fullWidth
                                        placeholder="Search by product / category / addon"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" } }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchRounded />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                {/* Actions */}
                                <Grid item xs={12} md={3}>
                                    <Typography
                                        sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8, textAlign: "center" }}
                                    >
                                        Actions
                                    </Typography>

                                    <Stack direction="row" justifyContent={"space-around"} spacing={1}>
                                        <Button
                                            variant="contained"
                                            disabled={loading}
                                            onClick={() => getAddonSales(1, false)}
                                            sx={{
                                                height: 52,
                                                width: "70%",
                                                borderRadius: 2,
                                                fontWeight: 800,
                                                textTransform: "none",
                                                backgroundColor: "#003049",
                                                "&:hover": { backgroundColor: "#00263a" },
                                            }}
                                        >
                                            {loading ? "Loading..." : "Get"}
                                        </Button>

                                        <CloudDownloadOutlined
                                            onClick={() => getAddonSales(1, true)}
                                            sx={{
                                                fontSize: "35px",
                                                cursor: "pointer",
                                                color: "#003049",
                                                display: "flex",
                                                alignSelf: "center",
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Content */}
                    <Card
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            border: "1px solid #e6eaf0",
                            overflow: "hidden",
                            background: "#fff",
                        }}
                    >
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                                    <CircularProgress />
                                    <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Report...</Typography>
                                </Box>
                            ) : filteredRows?.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: "center" }}>
                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                                        No addon sales report to display
                                    </Typography>
                                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                                        Select filters and click “Get” to view results.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: "1px solid #e6eaf0",
                                            background: "#fff",
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            useFlexGap
                                            flexWrap="wrap"
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                <Chip
                                                    icon={<FilterAltOutlined />}
                                                    label={`Rows: ${filteredRows?.length || 0}`}
                                                    variant="outlined"
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                />

                                                <Chip
                                                    label={`Total Addon Qty: ${legendTotals.totalAddonSalesCount}`}
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                    variant="outlined"
                                                />

                                                <Chip
                                                    label={`Taxable: ₹${legendTotals.totalTaxable.toFixed(2)}`}
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                    variant="outlined"
                                                />

                                                <Chip
                                                    label={`Discount: ₹${legendTotals.totalDiscount.toFixed(2)}`}
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                    variant="outlined"
                                                />

                                                <Chip
                                                    label={`Tax: ₹${legendTotals.totalTaxAmount.toFixed(2)}`}
                                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                                    variant="outlined"
                                                />
                                            </Stack>

                                            <Chip
                                                label={`Grand Total: ₹${legendTotals.totalGrandTotal.toFixed(2)}`}
                                                sx={{
                                                    fontWeight: 900,
                                                    borderRadius: 2,
                                                    backgroundColor: "#003049",
                                                    color: "#fff",
                                                    "& .MuiChip-label": { color: "#fff" },
                                                }}
                                            />
                                        </Stack>
                                    </Box>

                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            borderRadius: 0,
                                            maxHeight: "70vh",
                                            overflow: "auto",
                                        }}
                                    >
                                        <Table stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    {["No.", "Product", "Addon Group", "Values (counts)", "Total Addon Qty", "Taxable", "Discount", "Tax", "Grand Total"].map(
                                                        (h) => (
                                                            <TableCell
                                                                key={h}
                                                                sx={{
                                                                    fontWeight: 900,
                                                                    color: "#0f172a",
                                                                    background: "#f1f5f9",
                                                                    borderBottom: "1px solid #e2e8f0",
                                                                    whiteSpace: "nowrap",
                                                                    position: "sticky",
                                                                    top: 0,
                                                                    zIndex: 2,
                                                                }}
                                                                align={h === "Product" || h === "Values (counts)" ? "left" : "center"}
                                                            >
                                                                {h}
                                                            </TableCell>
                                                        )
                                                    )}
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {filteredRows.map((row, index) => (
                                                    <TableRow
                                                        key={`${row?.addonTitle}-${row?.productName}-${index}`}
                                                        hover
                                                        sx={{
                                                            backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                                            "& td": { borderBottom: "1px solid #f1f5f9" },
                                                        }}
                                                    >
                                                        <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                                                            {index + 1}
                                                        </TableCell>

                                                        <TableCell align="left">
                                                            <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a", textTransform: "capitalize" }}>
                                                                {row?.productName || "N/A"}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 12, color: "#64748b", textTransform: "capitalize" }}>
                                                                {row?.categoryName || "N/A"} • {row?.outletName || "N/A"}
                                                            </Typography>
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                                                            {row?.addonTitle || "N/A"}
                                                        </TableCell>

                                                        <TableCell align="left" sx={{ fontWeight: 700 }}>
                                                            {(row?.values || []).length ? (
                                                                <Stack spacing={0.5}>
                                                                    {row.values.map((v, i) => (
                                                                        <Typography key={i} sx={{ fontSize: 12, color: "#0f172a" }}>
                                                                            • {v?.selectedValue || "N/A"}: <b>{Number(v?.totalSaleCount || 0)}</b>
                                                                        </Typography>
                                                                    ))}
                                                                </Stack>
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 800 }}>
                                                            {Number(row?.totalAddonSalesCount || 0)}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                                            ₹{Number(row?.totalTaxable || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                                            ₹{Number(row?.totalDiscount || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                                            ₹{Number(row?.totalTaxAmount || 0).toFixed(2)}
                                                        </TableCell>

                                                        <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                                            ₹{Number(row?.totalGrandTotal || 0).toFixed(2)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Divider />

                                    <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                                        <Pagination
                                            count={pagination?.NumberOfPages || 1}
                                            page={currentPage}
                                            onChange={handlePageChange}
                                            color="primary"
                                            variant="outlined"
                                            shape="rounded"
                                        />
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

export default AddonSales;
